import { Navigate, useLocation } from "react-router-dom";
import type { ReactNode } from "react";
import { useTranslation } from "react-i18next";
import { useAuth } from "./AuthProvider";
import { expandRoles } from "./roles";
import type { UserRole } from "@/lib/supabase";

interface RoleGuardProps {
  /** Rôles autorisés. L'utilisateur doit en avoir au moins un. */
  allow: readonly UserRole[];
  /** Fallback route si l'utilisateur n'a pas le rôle (par défaut : /app). */
  fallbackTo?: string;
  children: ReactNode;
}

/**
 * Garde de route par rôle.
 *
 * Suppose qu'un `ProtectedRoute` a déjà vérifié l'authentification —
 * c'est-à-dire qu'on s'attend à être derrière la route `/app/*`.
 * Affiche un loader tant que les rôles ne sont pas chargés, puis redirige
 * si l'utilisateur ne porte aucun des rôles autorisés.
 */
export function RoleGuard({ allow, fallbackTo = "/app", children }: RoleGuardProps) {
  const { t } = useTranslation();
  const { roles, loading } = useAuth();
  const location = useLocation();

  if (loading) {
    return (
      <div role="status" className="grid min-h-[40vh] place-items-center">
        <div
          aria-hidden="true"
          className="size-8 animate-spin rounded-full border-2 border-brand border-t-transparent"
        />
        <span className="sr-only">{t("common.loading")}</span>
      </div>
    );
  }

  // Hiérarchie : admin_aon couvre chef_projet_aon + consultant_aon
  const effective = expandRoles(roles);
  const allowed = effective.some((r) => (allow as readonly string[]).includes(r));
  if (!allowed) {
    return <Navigate to={fallbackTo} state={{ from: location, denied: true }} replace />;
  }

  return <>{children}</>;
}
